import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import LinksComponent from "../../../../components/Tenant/LinksComponent/LinksComponent";
import { useGetCartonListMutation, useSaveCartonInfoMutation } from "../../../../features/tenant/carton/cartonApi";
import ModalComponent from "../../../../components/Tenant/ModalComponent/ModalComponent";
import { useGetAllCartonListQuery, useLazyGetAllCartonListQuery, useGetDistinctUnitsQuery } from "../../../../features/tenant/Lancer/LancerApi";
import TenantSlugs from "../../../../utils/TenantSlug";
import { exportToCSV } from "../../../../utils/exportCsv";

// ── helpers ────────────────────────────────────────────────────────────────
const fmt = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

// ── Product row inside an expanded carton card ─────────────────────────────
const ProductRow = ({ p, idx }) => (
  <tr className={idx % 2 === 0 ? "bg-white" : "bg-slate-50"}>
    <td className="px-4 py-2 text-xs text-slate-500 font-mono">{p.product_code || "—"}</td>
    <td className="px-4 py-2 text-xs font-medium text-slate-800">{p.product_name || p.name || "—"}</td>
    <td className="px-4 py-2 text-xs text-slate-600">{p.product_color || p.color || "—"}</td>
    <td className="px-4 py-2 text-xs text-slate-600">{p.product_size || p.size || "—"}</td>
    <td className="px-4 py-2 text-xs text-center font-semibold text-indigo-700">{p.original_qty ?? p.qty ?? "—"}</td>
    <td className="px-4 py-2 text-xs text-center font-semibold text-emerald-700">{p.qty ?? "—"}</td>
  </tr>
);

// ── Checkout badge ─────────────────────────────────────────────────────────
const CheckoutBadge = ({ checked }) =>
  checked ? (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
      Checked Out
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" /></svg>
      Pending
    </span>
  );

// ── Single carton card ─────────────────────────────────────────────────────
const CartonCard = ({ carton }) => {
  const [expanded, setExpanded] = useState(false);
  const products = carton.product_list || [];

  return (
    <div className={`rounded-xl border shadow-sm transition-all duration-200 overflow-hidden mb-3 ${carton.is_checked_out ? "border-emerald-200 bg-gradient-to-r from-emerald-50/40 to-white" : "border-slate-200 bg-white"}`}>

      {/* ── Card header ── */}
      <div
        className="flex items-center gap-4 px-5 py-4 cursor-pointer select-none hover:bg-slate-50/70 transition"
        onClick={() => setExpanded((e) => !e)}
      >
        {/* Expand icon */}
        <svg
          className={`w-4 h-4 text-slate-400 shrink-0 transition-transform duration-200 ${expanded ? "rotate-90" : ""}`}
          fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>

        {/* Carton number */}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-slate-800 font-mono truncate">{carton.carton_no}</p>
          <p className="text-xs text-slate-400 mt-0.5">{fmt(carton.created_at)}</p>
        </div>

        {/* Product summary pills */}
        <div className="hidden sm:flex flex-wrap gap-1 max-w-xs">
          {Array.from(new Set((products).map((p) => p.product_name || p.name).filter(Boolean))).slice(0, 2).map((n, i) => (
            <span key={i} className="px-2 py-0.5 text-xs rounded-full bg-indigo-100 text-indigo-700 border border-indigo-200 truncate max-w-[120px]" title={n}>{n}</span>
          ))}
          {products.length > 2 && (
            <span className="px-2 py-0.5 text-xs rounded-full bg-slate-100 text-slate-500">+{products.length - 2} more</span>
          )}
        </div>

        {/* Qty badge */}
        <div className="hidden md:flex flex-col items-center shrink-0">
          <span className="text-xs text-slate-400">QR Count</span>
          <span className="text-sm font-bold text-slate-800">{(carton.total_count ?? products.reduce((s, p) => s + (Number(p.original_qty) || 0), 0)) || "—"}</span>
        </div>

        {/* Checkout badge */}
        <div className="shrink-0">
          <CheckoutBadge checked={carton.is_checked_out} />
        </div>

        {/* Invoice number */}
        {carton.checkout_invoice_no && (
          <div className="hidden lg:flex flex-col items-end shrink-0">
            <span className="text-xs text-slate-400">Invoice</span>
            <span className="text-xs font-mono font-semibold text-slate-700">{carton.checkout_invoice_no}</span>
          </div>
        )}
      </div>

      {/* ── Expanded section ── */}
      {expanded && (
        <div className="border-t border-slate-100">

          {/* ── Products table ── */}
          <div className="px-5 pt-4 pb-2">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
              Products Inside ({products.length})
            </h4>
            {products.length > 0 ? (
              <div className="rounded-lg overflow-hidden border border-slate-200">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-100 text-slate-600 text-xs uppercase">
                    <tr>
                      <th className="px-4 py-2 font-semibold">Product Code</th>
                      <th className="px-4 py-2 font-semibold">Name</th>
                      <th className="px-4 py-2 font-semibold">Color</th>
                      <th className="px-4 py-2 font-semibold">Size</th>
                      <th className="px-4 py-2 font-semibold text-center">Original Qty</th>
                      <th className="px-4 py-2 font-semibold text-center">Remaining Qty</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {products.map((p, i) => <ProductRow key={p.id ?? i} p={p} idx={i} />)}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-xs text-slate-400 italic py-2">No product lines found for this carton.</p>
            )}
          </div>

          {/* ── Checkout details ── */}
          {carton.is_checked_out ? (
            <div className="mx-5 mb-4 mt-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
              <div className="flex items-center gap-2 mb-3">
                <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm font-bold text-emerald-800">Checkout Details</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-emerald-600 font-semibold uppercase tracking-wide">Distributor</p>
                  <p className="text-sm font-bold text-slate-800 mt-0.5">{carton.distributor_name || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-emerald-600 font-semibold uppercase tracking-wide">Mobile</p>
                  <p className="text-sm font-mono text-slate-700 mt-0.5">{carton.distributor_mobile || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-emerald-600 font-semibold uppercase tracking-wide">Distributor Code</p>
                  <p className="text-sm font-mono text-slate-700 mt-0.5">{carton.out_code || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-emerald-600 font-semibold uppercase tracking-wide">Checkout Ref</p>
                  <p className="text-sm font-mono text-slate-700 mt-0.5 truncate">{carton.checkout_ref || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-emerald-600 font-semibold uppercase tracking-wide">Checkout Date</p>
                  <p className="text-sm text-slate-700 mt-0.5">{fmt(carton.checkout_date)}</p>
                </div>
                <div>
                  <p className="text-xs text-emerald-600 font-semibold uppercase tracking-wide">Invoice No</p>
                  <p className="text-sm font-mono text-slate-700 mt-0.5">{carton.checkout_invoice_no || "—"}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="mx-5 mb-4 mt-3 rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-center gap-3">
              <svg className="w-5 h-5 text-amber-500 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <p className="text-sm font-semibold text-amber-800">Not yet checked out</p>
                {carton.checkout_invoice_no && (
                  <p className="text-xs text-amber-700 mt-0.5">Associated Invoice: <span className="font-mono font-bold">{carton.checkout_invoice_no}</span></p>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Main component ─────────────────────────────────────────────────────────
const CartonList = () => {
  const currentSlug = TenantSlugs.lancer;

  const [limit, setLimit] = useState(20);
  const [offset, setOffset] = useState(0);
  const [cartonSearch, setCartonSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedUnit, setSelectedUnit] = useState("");
  const [isCheckout, setIsCheckout] = useState(false);
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");

  const currentDate = new Date().toISOString().split("T")[0];
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncDates, setSyncDates] = useState({ DateFrom: currentDate, DateTo: currentDate });

  const [saveCartonInfo, { isLoading: isSyncing }] = useSaveCartonInfoMutation();
  const [fetchAllCartonsForExport, { isFetching: isExporting }] = useLazyGetAllCartonListQuery();

  const { data: unitsData } = useGetDistinctUnitsQuery({
    token: sessionStorage.getItem("access_token"),
    slug: currentSlug,
  });

  // Debounce
  useEffect(() => {
    const h = setTimeout(() => { setDebouncedSearch(cartonSearch); setOffset(0); }, 500);
    return () => clearTimeout(h);
  }, [cartonSearch]);

  const { data: cartonListData, isLoading, isFetching, error, refetch } = useGetAllCartonListQuery({
    token: sessionStorage.getItem("access_token"),
    slug: currentSlug,
    limit, offset,
    carton_no: debouncedSearch,
    unit: selectedUnit,
    checkout: isCheckout ? true : undefined,
    startDate: filterStartDate,
    endDate: filterEndDate,
  });

  const isDataLoading = isLoading || isFetching;

  useEffect(() => { if (error) toast.error("Failed to fetch carton list"); }, [error]);

  const myList = cartonListData?.body?.masterCartonList || [];
  const totalRows = cartonListData?.body?.totalCount || 0;

  const totalPages = Math.ceil(totalRows / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  const checkedOutCount = myList.filter((c) => c.is_checked_out).length;
  const pendingCount = myList.length - checkedOutCount;

  const handleExportCSV = async () => {
    try {
      const res = await fetchAllCartonsForExport({
        token: sessionStorage.getItem("access_token"),
        slug: currentSlug, limit: 1000000, offset: 0,
        carton_no: debouncedSearch, unit: selectedUnit,
        checkout: isCheckout ? true : undefined,
        startDate: filterStartDate, endDate: filterEndDate,
      }).unwrap();
      const exportData = res?.body?.masterCartonList || [];
      const exportColumns = [
        { name: "Carton Number", selector: (r) => r.carton_no },
        { name: "Article Name", selector: (r) => r.article_name },
        { name: "Product Code", selector: (r) => r.product_code },
        { name: "Color", selector: (r) => r.color },
        { name: "Size", selector: (r) => r.size },
        { name: "Checked Out", selector: (r) => r.is_checked_out ? "Yes" : "No" },
        { name: "Distributor", selector: (r) => r.distributor_name || "" },
        { name: "Distributor Mobile", selector: (r) => r.distributor_mobile || "" },
        { name: "Invoice No", selector: (r) => r.checkout_invoice_no || "" },
        { name: "Checkout Date", selector: (r) => r.checkout_date ? new Date(r.checkout_date).toLocaleString() : "" },
        { name: "Created Date", selector: (r) => r.created_at ? new Date(r.created_at).toLocaleString() : "" },
      ];
      exportToCSV(exportData, exportColumns, "carton_list.csv");
      toast.success("CSV Downloaded Successfully!");
    } catch { toast.error("Failed to export CSV"); }
  };

  const handleSyncSubmit = async () => {
    if (!syncDates.DateFrom || !syncDates.DateTo) { toast.error("Please select both dates"); return; }
    try {
      const response = await saveCartonInfo({
        token: sessionStorage.getItem("access_token"),
        body: { GlobalModifyCode: "", Doc_Codes: "", Doc_Codes2: "", DateFrom: syncDates.DateFrom, DateTo: syncDates.DateTo },
      }).unwrap();
      if (response.status === 200 || response.success) {
        toast.success(response.message || "Carton Info Synced Successfully");
        setShowSyncModal(false); refetch();
      } else { toast.error(response.message || "Failed to sync carton info"); }
    } catch (err) { toast.error(err?.data?.message || "An error occurred during sync"); }
  };

  return (
    <div className="flex w-full bg-gray-100 min-h-full justify-center">
      <div className="w-full">

        {/* ── Page header ── */}
        <div className="border-b bg-white border-gray-200 pl-6 py-3 shadow-sm flex justify-between items-center pr-6">
          <div>
            <h1 className="text-xl font-bold text-slate-800">Carton List</h1>
            <LinksComponent />
          </div>
          <div className="flex gap-2">
            <button
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 transition disabled:opacity-50"
              onClick={handleExportCSV} disabled={isExporting}
            >
              {isExporting ? "Exporting…" : "⬇ Export CSV"}
            </button>
            <button
              className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white shadow hover:bg-primary-600 transition"
              onClick={() => setShowSyncModal(true)}
            >
              ⟳ Sync Carton
            </button>
          </div>
        </div>

        <div className="p-4">

          {/* ── Stats strip ── */}
          <div className="mb-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: "Total Cartons", value: totalRows, color: "text-indigo-700", bg: "bg-indigo-50 border-indigo-200" },
              { label: "On This Page", value: myList.length, color: "text-slate-700", bg: "bg-slate-50 border-slate-200" },
              { label: "Checked Out", value: checkedOutCount, color: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200" },
              { label: "Pending", value: pendingCount, color: "text-amber-700", bg: "bg-amber-50 border-amber-200" },
            ].map((s) => (
              <div key={s.label} className={`rounded-xl border p-3 ${s.bg}`}>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{s.label}</p>
                <p className={`text-2xl font-extrabold mt-0.5 ${s.color}`}>{s.value}</p>
              </div>
            ))}
          </div>

          {/* ── Filters ── */}
          <div className="mb-4 flex flex-wrap gap-3 items-end bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Search Carton</label>
              <input
                type="text"
                className="w-48 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                placeholder="e.g. W26BG…"
                value={cartonSearch}
                onChange={(e) => setCartonSearch(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Unit</label>
              <select
                className="w-36 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                value={selectedUnit}
                onChange={(e) => { setSelectedUnit(e.target.value); setOffset(0); }}
              >
                <option value="">All Units</option>
                {unitsData?.body?.units?.map((u, i) => <option key={i} value={u.unit}>{u.unit}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Start Date</label>
              <input type="date" className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary" value={filterStartDate}
                onChange={(e) => { setFilterStartDate(e.target.value); setOffset(0); }} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">End Date</label>
              <input type="date" className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary" value={filterEndDate}
                onChange={(e) => { setFilterEndDate(e.target.value); setOffset(0); }} />
            </div>
            <div className="flex items-center gap-2 pb-0.5">
              <input type="checkbox" id="checkout-filter" className="w-4 h-4 rounded accent-primary cursor-pointer" checked={isCheckout}
                onChange={(e) => { setIsCheckout(e.target.checked); setOffset(0); }} />
              <label htmlFor="checkout-filter" className="text-sm font-medium text-slate-700 cursor-pointer flex items-center gap-1.5">
                Checked Out Only
                {isFetching && <div className="w-3.5 h-3.5 border-2 border-primary border-t-transparent rounded-full animate-spin" />}
              </label>
            </div>
            <button
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition"
              onClick={() => { setCartonSearch(""); setSelectedUnit(""); setIsCheckout(false); setFilterStartDate(""); setFilterEndDate(""); setOffset(0); }}
            >
              Clear
            </button>
          </div>

          {/* ── Carton card list ── */}
          {isDataLoading ? (
            <div className="flex flex-col items-center justify-center h-48 gap-3">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
              <p className="text-xs text-slate-400 font-medium">Loading cartons…</p>
            </div>
          ) : myList.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-slate-400">
              <svg className="w-12 h-12 mb-3 opacity-30" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10" />
              </svg>
              <p className="text-sm font-medium">No cartons found</p>
              <p className="text-xs mt-1">Try adjusting your filters</p>
            </div>
          ) : (
            <>
              {myList.map((c) => <CartonCard key={c.carton_no} carton={c} />)}

              {/* ── Pagination ── */}
              <div className="flex items-center justify-between mt-4 bg-white rounded-xl border border-slate-200 px-4 py-3 shadow-sm">
                <p className="text-sm text-slate-500">
                  Showing <span className="font-semibold text-slate-700">{offset + 1}–{Math.min(offset + limit, totalRows)}</span> of <span className="font-semibold text-slate-700">{totalRows}</span>
                </p>
                <div className="flex items-center gap-2">
                  <select className="border border-gray-300 rounded-lg px-2 py-1 text-sm" value={limit}
                    onChange={(e) => { setLimit(Number(e.target.value)); setOffset(0); }}>
                    {[10, 20, 50, 100].map((n) => <option key={n} value={n}>{n} / page</option>)}
                  </select>
                  <button className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 disabled:opacity-40 hover:bg-gray-50 transition" disabled={offset === 0}
                    onClick={() => setOffset((o) => Math.max(0, o - limit))}>
                    ← Prev
                  </button>
                  <span className="text-sm font-medium text-slate-700">{currentPage} / {totalPages || 1}</span>
                  <button className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 disabled:opacity-40 hover:bg-gray-50 transition" disabled={offset + limit >= totalRows}
                    onClick={() => setOffset((o) => o + limit)}>
                    Next →
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Sync modal ── */}
      {showSyncModal && (
        <ModalComponent show={showSyncModal} setShow={setShowSyncModal} size="md">
          <div className="p-6">
            <h3 className="text-lg font-bold mb-4 border-b pb-2">Sync Carton Info</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input type="date" className="w-full border border-gray-300 rounded-lg p-2 focus:ring-primary focus:border-primary"
                  value={syncDates.DateFrom} onChange={(e) => setSyncDates((p) => ({ ...p, DateFrom: e.target.value }))} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input type="date" className="w-full border border-gray-300 rounded-lg p-2 focus:ring-primary focus:border-primary"
                  value={syncDates.DateTo} onChange={(e) => setSyncDates((p) => ({ ...p, DateTo: e.target.value }))} />
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition"
                  onClick={() => setShowSyncModal(false)} disabled={isSyncing}>Cancel</button>
                <button className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition disabled:opacity-50"
                  onClick={handleSyncSubmit} disabled={isSyncing}>
                  {isSyncing ? "Syncing…" : "Submit"}
                </button>
              </div>
            </div>
          </div>
        </ModalComponent>
      )}
    </div>
  );
};

export default CartonList;
